import {
  MenuItemProps,
  toWSDateString,
  useModalContext,
  WSButton,
  WSButtonProps,
  WSContextMenu,
  WSElement,
  WSElementColorsType,
  WSElementProps,
  WSFlexBox,
  WSIcon,
  WSIconName,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  ClientWorkFlowStatus,
  InvoiceStatus,
  PayrollWorkflowStrategy
} from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import { useHistory, useLocation } from "react-router";
import { useUserId } from "../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import {
  useApprovePayable,
  useDownloadInvoicePdf,
  usePreApprovePayable,
  useSendPayable,
  useUnapprovePayable
} from "../../../query/payments/mutations";
import {
  useCollaboratorQuery,
  usePayrollSettings
} from "../../../query/payments/queries";
import {
  getCollaboratorEmail,
  getCollaboratorName,
  isPayableAllowedToEdit
} from "../../../query/payments/selectors";
import { useClientQuery } from "../../../query/users/queries";
import { WSQueries } from "../../../query/WSQuery";
import { openInNewTab } from "../../../utils/openInNewTab";
import { ADD_DEFAULT_PAYMENT_METHOD_WARNING_MODAL } from "../../screens/AddDefaultPaymentMethodWarningModal";
import { usePayableQuery } from "../../screens/payables/PayableDetails";
import { calculateLineItemsTotal } from "../../utils";
import {
  getDefaultPaymentMethodExists,
  getMatchingPayroll,
  getNextPayrollDate
} from "../../utils/payables";
import { DELETE_PAYABLE_MODAL } from "../DeletePayableModal/DeletePayableModal";
import {
  InvoiceStatusTimeline,
  InvoiceTimelineStatus
} from "../InvoiceStatusTimeline/InvoiceStatusTimeline";
import { PAYABLES_BULK_UNAPPROVE } from "../NewPayablesTable/BulkUnapproveModal";
import { REJECT_PAYABLE_MODAL } from "../RejectPayableModal/RejectPayableModal";

type Props = {
  payableId: string;
} & WSElementProps;

export const PayableDetailsHeader: React.FC<Props> = ({
  payableId,
  ...elementProps
}) => {
  const payableQuery = usePayableQuery(payableId);
  const collaboratorQuery = useCollaboratorQuery(
    payableQuery.data?.collaboratorId as string,
    {
      retry: false,
      enabled: !!payableQuery.data?.collaboratorId
    }
  );
  const userId = useUserId();
  const clientQuery = useClientQuery(userId);
  const featureFlagsQuery = useFeatureFlags();
  const payrollSettingsQuery = usePayrollSettings(userId);

  const [sendPayable, sendPayableMeta] = useSendPayable();
  const [preApprovePayable, preApprovePayableMeta] = usePreApprovePayable();
  const [approvePayable, approvePayableMeta] = useApprovePayable();
  const [unapprovePayable, unapprovePayableMeta] = useUnapprovePayable();
  const { openModal } = useModalContext();
  const [downloadInvoicePdf, downloadInvoicePdfMeta] = useDownloadInvoicePdf();

  const history = useHistory();
  const location = useLocation();

  return (
    <WSElement {...elementProps}>
      <WSQueries
        queries={{
          payableQuery,
          collaboratorQuery,
          featureFlagsQuery,
          payrollSettingsQuery,
          clientQuery
        }}
      >
        {({
          payableQuery: { data: payable },
          collaboratorQuery: { data: collaborator },
          featureFlagsQuery: { data: featureFlags },
          payrollSettingsQuery: { data: payrollSettings },
          clientQuery: { data: client }
        }) => {
          const openNowButtonProps = {
            children: "Open now",
            onClick: () => {
              sendPayable({ payableId, action: "open" });
            },
            loading: sendPayableMeta.isLoading
          };

          const preApproveNowButtonProps = {
            children: "Pre-Approve now",
            onClick: () => {
              preApprovePayable(payableId);
            },
            loading: preApprovePayableMeta.isLoading
          };

          const approveNowButtonProps = {
            children: "Approve now",
            onClick: () => {
              if (getDefaultPaymentMethodExists(client, payrollSettings)) {
                approvePayable(payableId);
              } else {
                openModal(ADD_DEFAULT_PAYMENT_METHOD_WARNING_MODAL, {
                  nextUrl: "/member/invoices/payables"
                });
              }
            },
            loading: approvePayableMeta.isLoading
          };

          const unapproveButtonProps = {
            children: "Unapprove",
            onClick: () => {
              unapprovePayable(payableId);
            },
            loading: unapprovePayableMeta.isLoading
          };

          const downloadPDFButtonProps = {
            children: "Download PDF",
            onClick: () => {
              downloadInvoicePdf({ invoiceId: payable.payableId });
            },
            loading: downloadInvoicePdfMeta.isLoading
          };

          const editButtonProps = {
            children: "Edit",
            onClick: () => {
              history.push({
                pathname: `/member/invoices/payables/${payableId}/edit`,
                state: { backPath: location.pathname }
              });
            }
          };

          let icon: WSIconName | undefined;
          let iconColor: WSElementColorsType | undefined;
          let header: string;
          let subheader: string;
          let status: InvoiceTimelineStatus | undefined;
          let buttonProps: WSButtonProps | null = null;

          if (payable.status === InvoiceStatus.Open) {
            if (
              payable.client.workflowStatus === ClientWorkFlowStatus.Approved
            ) {
              header = "Approved for payment";
              subheader = `Will be paid on ${toWSDateString(
                getMatchingPayroll(payrollSettings, payable)?.date,
                "monthDate"
              )}`;
              buttonProps = unapproveButtonProps;
              status = InvoiceTimelineStatus.Approved;
            } else {
              if (
                payrollSettings.workflow === PayrollWorkflowStrategy.DualStage
              ) {
                if (
                  payable.client.workflowStatus ===
                  ClientWorkFlowStatus.PreApproved
                ) {
                  header = "Invoice open";
                  subheader = "Approve or pay now";
                  buttonProps = approveNowButtonProps;
                  status = InvoiceTimelineStatus.PreApproved;
                } else {
                  header = "Invoice open";
                  subheader = "Pre-Approve";
                  buttonProps = preApproveNowButtonProps;
                  status = InvoiceTimelineStatus.Open;
                }
              } else {
                header = "Invoice open";
                subheader = "Approve or pay now";
                buttonProps = approveNowButtonProps;
                status = InvoiceTimelineStatus.Open;
              }
            }
          } else if (payable.status === InvoiceStatus.Overdue) {
            if (
              payable.client.workflowStatus === ClientWorkFlowStatus.Approved
            ) {
              header = "Approved for payment";
              subheader = `Will be paid on ${toWSDateString(
                getMatchingPayroll(payrollSettings, payable)?.date,
                "monthDate"
              )}`;
              buttonProps = unapproveButtonProps;
              status = InvoiceTimelineStatus.Approved;
            } else {
              if (
                payrollSettings.workflow === PayrollWorkflowStrategy.DualStage
              ) {
                if (
                  payable.client.workflowStatus ===
                  ClientWorkFlowStatus.PreApproved
                ) {
                  icon = "info";
                  iconColor = "yellow500";
                  header = "Invoice payment is overdue";
                  subheader = "Approve or pay now";
                  buttonProps = approveNowButtonProps;
                  status = InvoiceTimelineStatus.PreApproved;
                } else {
                  icon = "info";
                  iconColor = "yellow500";
                  header = "Invoice payment is overdue";
                  subheader = "Pre-Approve now";
                  buttonProps = preApproveNowButtonProps;
                  status = InvoiceTimelineStatus.Open;
                }
              } else {
                icon = "info";
                iconColor = "yellow500";
                header = "Invoice payment is overdue";
                subheader = "Approve or pay now";
                buttonProps = approveNowButtonProps;
                status = InvoiceTimelineStatus.Open;
              }
            }
          } else if (payable.status === InvoiceStatus.Paid) {
            header = "Invoice Paid";
            subheader = payable.events.depositedAt
              ? `Invoice was deposited on ${toWSDateString(
                  payable.events.depositedAt,
                  "monthDate"
                )}`
              : payable.events.paidAt
              ? `Invoice was paid on ${toWSDateString(
                  payable.events.paidAt,
                  "monthDate"
                )}`
              : "Invoice was paid";
            buttonProps = downloadPDFButtonProps;

            status = payable.events.depositedAt
              ? InvoiceTimelineStatus.Deposited
              : InvoiceTimelineStatus.Paid;
          } else if (payable.status === InvoiceStatus.PaymentInTransit) {
            header = "Waiting on wire to arrive";
            subheader = "Invoice was marked paid via bank transfer";
          } else if (payable.status === InvoiceStatus.Pending) {
            if (
              payable.client.workflowStatus ===
                ClientWorkFlowStatus.PaymentInitiated ||
              payable.client.workflowStatus === ClientWorkFlowStatus.Funded
            ) {
              header = "Funded for payment";
              subheader =
                "Funded and waiting on collaborator to enable digital payments";
              buttonProps = downloadPDFButtonProps;
              status = InvoiceTimelineStatus.Paid;
            } else if (
              payable.client.workflowStatus === ClientWorkFlowStatus.Approved
            ) {
              header = "Approved for payment";
              subheader = `Will be funded on ${toWSDateString(
                getNextPayrollDate(payrollSettings),
                "monthDate"
              )}`;
              buttonProps = unapproveButtonProps;
              status = InvoiceTimelineStatus.Approved;
            } else {
              if (
                payrollSettings.workflow === PayrollWorkflowStrategy.DualStage
              ) {
                if (
                  payable.client.workflowStatus ===
                  ClientWorkFlowStatus.PreApproved
                ) {
                  header = "Eligibility pending";
                  subheader =
                    "Waiting on collaborator to enable digital payments";
                  buttonProps = approveNowButtonProps;
                  status = InvoiceTimelineStatus.PreApproved;
                } else {
                  header = "Eligibility pending";
                  subheader =
                    "Waiting on collaborator to enable digital payments";
                  buttonProps = preApproveNowButtonProps;
                  status = InvoiceTimelineStatus.Open;
                }
              } else {
                header = "Eligibility pending";
                subheader =
                  "Waiting on collaborator to enable digital payments";
                buttonProps = approveNowButtonProps;
                status = InvoiceTimelineStatus.Open;
              }
            }
          } else {
            header = "This is a draft";
            subheader = "Open invoice to pay";
            buttonProps =
              calculateLineItemsTotal(payable.lineItems) > 0
                ? openNowButtonProps
                : editButtonProps;
          }

          const menuItems: MenuItemProps[] = [];

          if (
            [ClientWorkFlowStatus.PreApproved].includes(
              payable.client.workflowStatus
            )
          ) {
            menuItems.push({
              icon: "stop",
              label: "Unapprove",
              onClick: () => {
                openModal(PAYABLES_BULK_UNAPPROVE, {
                  data: [{ payable }]
                });
              }
            });
          }

          if (
            [
              InvoiceStatus.Draft,
              InvoiceStatus.Open,
              InvoiceStatus.Overdue,
              InvoiceStatus.Pending
            ].includes(payable.status)
          ) {
            menuItems.push({
              icon: "pencil",
              label: "Edit",
              onClick: () => {
                history.push({
                  pathname: `/member/invoices/payables/${payableId}/edit`,
                  state: { backPath: location.pathname }
                });
              }
            });
          }

          if (
            [
              InvoiceStatus.Pending,
              InvoiceStatus.Open,
              InvoiceStatus.Overdue
            ].includes(payable.status) &&
            payable.labels.createdBy !== userId &&
            (!payable.client.workflowStatus ||
              payable.client.workflowStatus === ClientWorkFlowStatus.Pending ||
              payable.client.workflowStatus === ClientWorkFlowStatus.Declined)
          ) {
            menuItems.push({
              icon: "close",
              label: "Reject",
              onClick: () => {
                openModal(REJECT_PAYABLE_MODAL, {
                  collaborator,
                  payable
                });
              }
            });
          }

          if (
            [InvoiceStatus.Draft].includes(payable.status) &&
            isPayableAllowedToEdit(payable, userId)
          ) {
            menuItems.push({
              icon: "close",
              label: "Remove",
              onClick: () => {
                openModal(DELETE_PAYABLE_MODAL, {
                  collaborator,
                  payable
                });
              }
            });
          }

          if (
            [
              InvoiceStatus.Open,
              InvoiceStatus.Overdue,
              InvoiceStatus.Pending
            ].includes(payable.status) &&
            isPayableAllowedToEdit(payable, userId)
          ) {
            menuItems.push({
              icon: "close",
              label: "Cancel invoice",
              onClick: () => {
                openModal(DELETE_PAYABLE_MODAL, {
                  collaborator,
                  payable
                });
              }
            });
          }

          if (
            [
              InvoiceStatus.Open,
              InvoiceStatus.Overdue,
              InvoiceStatus.Paid
            ].includes(payable.status)
          ) {
            menuItems.push({
              icon: "download",
              label: "Download PDF",
              onClick: () => {
                downloadInvoicePdf({ invoiceId: payable.payableId });
              }
            });
          }

          if (
            payable.attachments?.invoiceLink &&
            [InvoiceStatus.Open, InvoiceStatus.Overdue].includes(payable.status)
          ) {
            menuItems.push({
              icon: "moneyRounded",
              label: "Pay invoice",
              onClick: () => {
                if (payable.attachments?.invoiceLink) {
                  openInNewTab(payable.attachments.invoiceLink);
                }
              }
            });
          }

          let timelineStatuses = [
            InvoiceTimelineStatus.Open,
            InvoiceTimelineStatus.Approved,
            InvoiceTimelineStatus.Paid,
            InvoiceTimelineStatus.Deposited
          ];
          if (payrollSettings.workflow === PayrollWorkflowStrategy.DualStage) {
            timelineStatuses = [
              InvoiceTimelineStatus.Open,
              InvoiceTimelineStatus.PreApproved,
              InvoiceTimelineStatus.Approved,
              InvoiceTimelineStatus.Paid,
              InvoiceTimelineStatus.Deposited
            ];
          }

          return (
            <>
              <WSFlexBox.CenterY mb="XS">
                {icon && <WSIcon name={icon} color={iconColor} mr="S" />}
                <WSText.Heading3>{header}</WSText.Heading3>
              </WSFlexBox.CenterY>
              <WSText>{subheader}</WSText>
              <WSText.Paragraph mt="S">
                {getCollaboratorName(collaborator)}
              </WSText.Paragraph>
              <WSText color="grey600" mb="M">
                {getCollaboratorEmail(collaborator)}
              </WSText>

              <InvoiceStatusTimeline
                currentStatus={status}
                statuses={timelineStatuses}
                mb="M"
              />

              <WSFlexBox wrap="nowrap" alignItems="center">
                {buttonProps && (
                  <WSButton
                    fullWidth
                    {...buttonProps}
                    mr={menuItems.length > 0 ? "S" : undefined}
                  />
                )}
                {menuItems.length > 0 && (
                  <WSContextMenu menuItems={menuItems} name="payableActions" />
                )}
              </WSFlexBox>
            </>
          );
        }}
      </WSQueries>
    </WSElement>
  );
};
